import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Alert,
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import vehiclesService from "services/vehiclesService";
import formsService from "services/formsService";
import { Hub, KeyValue } from "common/models";
import { Vehicle } from "vehicles/models";
import { VehicleServiceUpdateRequest } from "forms/models";
import { toast } from "react-toastify";
import * as Yup from "yup";
import moment, { Moment } from "moment";

const formatOosDate = (date: Moment | Date) => moment(date).format("MM/DD/YY");

const buildOosNotesHeader = (
  oosDate: Moment | Date,
  userName: string,
  shop: string,
  returnDate: Moment | Date
) => {
  const trimmedShop = shop?.trim();
  const shopSegment = trimmedShop ? ` Shop: ${trimmedShop}.` : "";
  return `OOS on ${formatOosDate(oosDate)} by ${userName || "Unknown"}.${shopSegment} Back in service on ${formatOosDate(returnDate)}`;
};

interface VehicleOption {
  id: number;
  unitNumber: number;
}

interface FormData {
  locationId: number | null;
  unitNumber: number | null;
  statusId: number | null;
  reasonId: number | null;
  shop: string;
  notes: string;
  expectedReturnDate: Moment | null;
}

interface FormErrors {
  locationId?: string;
  unitNumber?: string;
  statusId?: string;
  reasonId?: string;
  shop?: string;
  notes?: string;
  expectedReturnDate?: string;
}

const getValidationSchema = (isWorkingStatus: boolean) =>
  Yup.object().shape({
    locationId: Yup.number().nullable().required("Location is required"),
    unitNumber: Yup.number().nullable().required("Vehicle is required"),
    statusId: Yup.number().nullable().required("Status is required"),
    reasonId: isWorkingStatus
      ? Yup.number().nullable()
      : Yup.number().nullable().required("Reason is required"),
    shop: isWorkingStatus ? Yup.string() : Yup.string().required("Shop is required"),
    notes: isWorkingStatus ? Yup.string() : Yup.string().required("Notes is required"),
    expectedReturnDate: isWorkingStatus
      ? Yup.date().nullable()
      : Yup.date()
          .nullable()
          .required("Expected Return to Service is required")
          .min(moment().startOf("day").toDate(), "Expected Return to Service cannot be in the past"),
  });

const VehicleServiceUpdate = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userName = accounts[0]?.name || accounts[0]?.username || "";
  const [locations, setLocations] = useState<Hub[]>([]);
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [selectedVehicleDetail, setSelectedVehicleDetail] = useState<Vehicle | null>(null);
  const [statuses, setStatuses] = useState<KeyValue[]>([]);
  const [reasons, setReasons] = useState<KeyValue[]>([]);
  const [formData, setFormData] = useState<FormData>({
    locationId: null,
    unitNumber: null,
    statusId: null,
    reasonId: null,
    shop: "",
    notes: "",
    expectedReturnDate: null,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oosDays, setOosDays] = useState<number | null>(null);

  const workingStatusId = statuses.find((s) => s.name === "Working")?.id ?? null;
  const selectedVehicleCurrentStatus =
    selectedVehicleDetail?.statusId != null ? Number(selectedVehicleDetail.statusId) : null;
  const isCurrentlyOutOfService =
    !!selectedVehicleDetail &&
    selectedVehicleCurrentStatus !== null &&
    workingStatusId !== null &&
    selectedVehicleCurrentStatus !== workingStatusId;
  const availableStatuses = statuses.filter((s) => s.id !== selectedVehicleCurrentStatus);
  const isWorkingStatus = statuses.find((s) => s.id === formData.statusId)?.name === "Working";
  const availableReasons = reasons.filter((r) => r.name?.trim().toLowerCase() !== "working");

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.locationId) {
      loadVehiclesByHub(formData.locationId);
    } else {
      setVehicles([]);
    }
  }, [formData.locationId]);

  useEffect(() => {
    if (formData.unitNumber) {
      vehiclesService
        .getVehicle(formData.unitNumber)
        .then((data) => setSelectedVehicleDetail(data))
        .catch(() => {
          setSelectedVehicleDetail(null);
          toast.error("Failed to load vehicle details");
        });
    } else {
      setSelectedVehicleDetail(null);
    }
  }, [formData.unitNumber]);

  const loadData = async () => {
    try {
      const [hubsData, statusesData, reasonsData] = await Promise.all([
        orgDataService.getAllHubs(),
        orgDataService.getVehicleStatuses(),
        orgDataService.getOutOfServiceReasons(),
      ]);
      setLocations(hubsData);
      setStatuses(statusesData);
      setReasons(reasonsData);
    } catch (error) {
      toast.error("Failed to load form data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadVehiclesByHub = async (hubId: number) => {
    setIsLoadingVehicles(true);
    try {
      const vehiclesData = await vehiclesService.getVehiclesByHub(hubId);
      setVehicles(
        vehiclesData.map((v: any) => ({
          id: v.id,
          unitNumber: v.unitNumber,
        }))
      );
    } catch (error) {
      toast.error("Failed to load vehicles");
      setVehicles([]);
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  const validate = (): boolean => {
    try {
      const validationSchema = getValidationSchema(isWorkingStatus);
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validation = error as Yup.ValidationError;
      const newErrors: FormErrors = {};
      validation.inner.forEach((err) => {
        if (err.path) {
          newErrors[err.path as keyof FormErrors] = err.message;
        }
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      let notesToSubmit: string | undefined;
      if (!isWorkingStatus) {
        const header = buildOosNotesHeader(moment(), userName, formData.shop, formData.expectedReturnDate!);
        const extra = formData.notes?.trim();
        notesToSubmit = extra ? `${header}\n${extra}` : header;
      }

      const request: VehicleServiceUpdateRequest = {
        locationId: formData.locationId!,
        unitNumber: formData.unitNumber!,
        currentMileage: selectedVehicleDetail?.odometer ?? 0,
        statusId: formData.statusId!,
        reasonId: isWorkingStatus ? 0 : formData.reasonId!,
        shop: isWorkingStatus ? undefined : formData.shop,
        notes: notesToSubmit,
        expectedReturnDate: isWorkingStatus ? undefined : formData.expectedReturnDate!.toISOString(),
      };

      const response = await formsService.submitVehicleServiceUpdate(request);

      if (response.success) {
        navigate("/forms/vehicle-service-update/success", {
          state: {
            message: response.message,
            vehicleUnitNumber: formData.unitNumber,
          },
        });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to submit vehicle service update");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExtendOos = async () => {
    if (isSubmitting) return;
    if (!selectedVehicleDetail || !isCurrentlyOutOfService) return;
    if (!formData.locationId || !formData.unitNumber) {
      toast.error("Select a location and vehicle first");
      return;
    }

    const mileage = selectedVehicleDetail.odometer ?? 0;

    const now = moment();
    const newReturn = now.clone().add(48, "hours");
    const header = buildOosNotesHeader(now, userName, formData.shop, newReturn);
    const extra = formData.notes?.trim();
    const notesToSubmit = extra ? `${header}\n${extra}` : header;

    setIsSubmitting(true);
    try {
      const request: VehicleServiceUpdateRequest = {
        locationId: formData.locationId,
        unitNumber: formData.unitNumber,
        currentMileage: Number(mileage),
        statusId: Number(selectedVehicleDetail.statusId),
        reasonId: selectedVehicleDetail.outOfServiceReasonId ?? 0,
        shop: formData.shop || undefined,
        notes: notesToSubmit,
        expectedReturnDate: newReturn.toISOString(),
        // Append to the vehicle's existing comments on the server so the prior note is preserved.
        appendNotes: true,
      };

      const response = await formsService.submitVehicleServiceUpdate(request);

      if (response.success) {
        navigate("/forms/vehicle-service-update/success", {
          state: {
            message: response.message,
            vehicleUnitNumber: formData.unitNumber,
          },
        });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to extend OOS");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === "locationId") {
      setFormData({ ...formData, [field]: value, unitNumber: null, statusId: null });
      setErrors({ ...errors, locationId: undefined, unitNumber: undefined, statusId: undefined });
    } else if (field === "unitNumber") {
      setFormData({ ...formData, [field]: value, statusId: null });
      setErrors({ ...errors, unitNumber: undefined, statusId: undefined });
    } else if (field === "statusId") {
      const newStatusName = statuses.find((s) => s.id === value)?.name;
      const becomingWorking = newStatusName === "Working";
      setFormData({
        ...formData,
        [field]: value,
        reasonId: becomingWorking ? null : formData.reasonId,
        shop: becomingWorking ? "" : formData.shop,
      });
      setErrors({ ...errors, statusId: undefined, reasonId: undefined, shop: undefined });
    } else if (field === "reasonId") {
      setFormData({ ...formData, [field]: value });
      setErrors({ ...errors, reasonId: undefined });
    } else {
      setFormData({ ...formData, [field]: value });
      if (errors[field]) {
        setErrors({ ...errors, [field]: undefined });
      }
    }
  };

  const handleSelectOosDays = (days: number) => {
    setOosDays(days);
    handleChange("expectedReturnDate", moment().add(days, "days"));
  };

  if (isLoading) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Vehicle Service Update
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Update vehicle service and maintenance records
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.locationId}>
                <InputLabel id="location-label">Location *</InputLabel>
                <Select
                  labelId="location-label"
                  id="locationId"
                  value={formData.locationId ?? ""}
                  label="Location *"
                  onChange={(e) => handleChange("locationId", e.target.value)}
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationId && <FormHelperText>{errors.locationId}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.unitNumber} disabled={!formData.locationId || isLoadingVehicles}>
                <InputLabel id="vehicle-label">
                  {isLoadingVehicles ? "Loading vehicles..." : "Vehicle *"}
                </InputLabel>
                <Select
                  labelId="vehicle-label"
                  id="unitNumber"
                  value={formData.unitNumber ?? ""}
                  label={isLoadingVehicles ? "Loading vehicles..." : "Vehicle *"}
                  onChange={(e) => handleChange("unitNumber", e.target.value)}
                >
                  {vehicles.map((vehicle) => (
                    <MenuItem key={vehicle.unitNumber} value={vehicle.unitNumber}>
                      {vehicle.unitNumber}
                    </MenuItem>
                  ))}
                </Select>
                {errors.unitNumber && <FormHelperText>{errors.unitNumber}</FormHelperText>}
                {!formData.locationId && !errors.unitNumber && (
                  <FormHelperText>Select a location first</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.statusId}>
                <InputLabel id="status-label">Status *</InputLabel>
                <Select
                  labelId="status-label"
                  id="statusId"
                  value={formData.statusId ?? ""}
                  label="Status *"
                  onChange={(e) => handleChange("statusId", e.target.value)}
                >
                  {availableStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.statusId && <FormHelperText>{errors.statusId}</FormHelperText>}
              </FormControl>
            </Grid>

            {!isWorkingStatus && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.reasonId}>
                  <InputLabel id="reason-label">Reason *</InputLabel>
                  <Select
                    labelId="reason-label"
                    id="reasonId"
                    value={formData.reasonId ?? ""}
                    label="Reason *"
                    onChange={(e) => handleChange("reasonId", e.target.value)}
                  >
                    {availableReasons.map((reason) => (
                      <MenuItem key={reason.id} value={reason.id}>
                        {reason.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.reasonId && <FormHelperText>{errors.reasonId}</FormHelperText>}
                </FormControl>
              </Grid>
            )}

            {!isWorkingStatus && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Shop *"
                    value={formData.shop}
                    onChange={(e) => handleChange("shop", e.target.value)}
                    error={!!errors.shop}
                    helperText={errors.shop}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Notes *"
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    error={!!errors.notes}
                    helperText={
                      errors.notes ||
                      "An OOS summary line is added automatically. Use this field for any additional context."
                    }
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <MDTypography variant="body2" fontWeight="medium" mb={1}>
                    Expected Return to Service *
                  </MDTypography>
                  <Box display="flex" gap={1}>
                    {[1, 2, 3].map((days) => (
                      <MDButton
                        key={days}
                        variant={oosDays === days ? "contained" : "outlined"}
                        color="dark"
                        onClick={() => handleSelectOosDays(days)}
                      >
                        {days === 1 ? "1 Day" : `${days} Days`}
                      </MDButton>
                    ))}
                  </Box>
                  {errors.expectedReturnDate && (
                    <FormHelperText error>{errors.expectedReturnDate}</FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Total Expected Hours OOS"
                    value={
                      formData.expectedReturnDate
                        ? Math.max(0, Math.round(moment(formData.expectedReturnDate).diff(moment(), "hours", true)))
                        : ""
                    }
                    InputProps={{
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {isCurrentlyOutOfService && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity="warning"
                action={
                  <MDButton
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={handleExtendOos}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Extending..." : "Extend OOS 48 hours"}
                  </MDButton>
                }
              >
                This vehicle is currently out of service. You can extend the return-to-service date by 48 hours
                without filling out the full form.
              </Alert>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")} disabled={isSubmitting}>
              Cancel
            </MDButton>
            <MDButton variant="contained" color="dark" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </MDButton>
          </Box>
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default VehicleServiceUpdate;
